import { useCallback, useEffect, useRef, useState } from 'react';
import { BattleRoomConflictError, fetchBattleRoom, putBattleRoom } from 'features/battle/room-api';
import {
  emptyHostRoom,
  ensureBattle,
  newBattleRoomId,
  seatForPlayer,
  replayTrail,
  startPvpBattle,
  concedeLiveRoom,
  roomNeedsLeaveOnPageHide,
  withAbandonedBy,
  BATTLE_TRAIL_LENGTH,
  type BattleRoom,
} from 'features/battle/room';
import type { BattleInput, BattleState, PlayerId } from 'features/battle/types';

const POLL_MS = 1500;
const OPENING_POLL_MS = 500;
const MISSING_AFTER_MS = 20000;
const CREATE_ATTEMPTS = 8;

export type BattleRoomStatus = 'idle' | 'loading' | 'missing' | 'ready';

/**
 * `stepDelayMs` paces replays: when a poll lands several opponent moves at once, each in-between
 * state shows for that long before the next, so plays and their activations still read one by one.
 */
export const useBattleRoom = (
  roomId: string | null,
  playerId: string,
  playerName: string,
  stepDelayMs: (state: BattleState) => number
) => {
  const [room, setRoom] = useState<BattleRoom | null>(null);
  /** Latest room, including while `room` is still replaying an earlier step. */
  const [committed, setCommitted] = useState<BattleRoom | null>(null);
  const replayQueueRef = useRef<BattleState[]>([]);
  const replayTimerRef = useRef(0);
  const stepDelayRef = useRef(stepDelayMs);
  stepDelayRef.current = stepDelayMs;
  const [status, setStatus] = useState<BattleRoomStatus>('idle');
  const seqRef = useRef(0);
  const roomRef = useRef<BattleRoom | null>(null);
  const missingSinceRef = useRef(0);
  const writeEpochRef = useRef(0);
  const writeQueueRef = useRef(Promise.resolve());
  const writeEpochRoomRef = useRef<string | null>(null);

  const stopReplay = useCallback(() => {
    window.clearTimeout(replayTimerRef.current);
    replayQueueRef.current = [];
  }, []);

  const replaceRoom = useCallback(
    (next: BattleRoom) => {
      stopReplay();
      seqRef.current = next.seq;
      roomRef.current = next;
      setCommitted(next);
      setRoom(next);
      setStatus('ready');
    },
    [stopReplay]
  );

  /** Shows queued states one at a time. `roomRef` already holds the latest room, so writes never use a replayed one. */
  const showNextReplayed = useCallback(() => {
    const state = replayQueueRef.current.shift();
    const latest = roomRef.current;
    if (!state || !latest) return;
    setRoom(replayQueueRef.current.length ? { ...latest, state } : latest);
    if (replayQueueRef.current.length) {
      replayTimerRef.current = window.setTimeout(showNextReplayed, stepDelayRef.current(state));
    }
  }, []);

  const applyRemote = useCallback(
    (incoming: BattleRoom, force = false) => {
      if (!force && roomRef.current?.abandonedBy && !incoming.abandonedBy) return;
      if (incoming.abandonedBy) {
        replaceRoom(incoming);
        return;
      }
      if (!force) {
        if (incoming.seq < seqRef.current) return;
        if (incoming.seq === seqRef.current && roomRef.current) return;
      }
      const replaying = replayQueueRef.current.length > 0;
      const steps = force ? [] : replayTrail(roomRef.current, incoming);
      if (!steps.length) {
        replaceRoom(incoming);
        return;
      }
      seqRef.current = incoming.seq;
      roomRef.current = incoming;
      setCommitted(incoming);
      setStatus('ready');
      replayQueueRef.current.push(...steps);
      if (!replaying) showNextReplayed();
    },
    [replaceRoom, showNextReplayed]
  );

  const enqueueWrite = useCallback(
    (room: BattleRoom) => {
      const epochAtQueue = writeEpochRef.current;
      writeQueueRef.current = writeQueueRef.current
        .then(async () => {
          if (epochAtQueue !== writeEpochRef.current) return;
          try {
            await putBattleRoom(room);
          } catch (error) {
            if (error instanceof BattleRoomConflictError && epochAtQueue === writeEpochRef.current) {
              writeEpochRef.current += 1;
              applyRemote(error.room, true);
            }
          }
        })
        .catch(() => undefined);
    },
    [applyRemote]
  );

  useEffect(() => {
    if (writeEpochRoomRef.current !== roomId) {
      writeEpochRoomRef.current = roomId;
      writeEpochRef.current += 1;
    }
    if (!roomId) {
      roomRef.current = null;
      seqRef.current = 0;
      missingSinceRef.current = 0;
      setCommitted(null);
      setRoom(null);
      setStatus('idle');
      return;
    }
    let cancelled = false;
    let timer = 0;
    if (roomRef.current?.id !== roomId) {
      seqRef.current = 0;
      roomRef.current = null;
      missingSinceRef.current = 0;
      setCommitted(null);
      setRoom(null);
      setStatus('loading');
    } else {
      missingSinceRef.current = 0;
    }

    const tick = async () => {
      let wait = POLL_MS;
      try {
        let next = await fetchBattleRoom(roomId);
        if (cancelled) return;
        if (!next) {
          // Fresh writes 404 for a bit on Netlify Blobs. Keep local state and keep polling.
          if (roomRef.current?.id !== roomId) {
            if (!missingSinceRef.current) missingSinceRef.current = Date.now();
            const waited = Date.now() - missingSinceRef.current;
            setStatus(waited >= MISSING_AFTER_MS ? 'missing' : 'loading');
            wait = waited >= MISSING_AFTER_MS ? POLL_MS : OPENING_POLL_MS;
          }
        } else {
          missingSinceRef.current = 0;
          if (next.guest && !next.state && !next.abandonedBy) {
            next = { ...ensureBattle(next), seq: next.seq + 1 };
            try {
              await putBattleRoom(next);
            } catch {
              // Honor-system mailbox: the other client may have written the same battle.
            }
          }
          if (cancelled) return;
          applyRemote(next);
        }
      } catch {
        if (!cancelled && !roomRef.current) setStatus('loading');
        wait = roomRef.current ? POLL_MS : OPENING_POLL_MS;
      }
      if (!cancelled) {
        timer = window.setTimeout(() => {
          void tick();
        }, wait);
      }
    };

    void tick();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      stopReplay();
    };
  }, [applyRemote, roomId, stopReplay]);

  const createChallenge = useCallback(
    async (deck: string[], flags: { hostDeckPure: boolean; pureOnlyBattle: boolean }): Promise<string> => {
      for (let attempt = 0; attempt < CREATE_ATTEMPTS; attempt += 1) {
        const id = newBattleRoomId();
        const next = emptyHostRoom({ id, playerId, name: playerName, deck, ...flags });
        try {
          await putBattleRoom(next);
          replaceRoom(next);
          return id;
        } catch (error) {
          if (error instanceof BattleRoomConflictError) continue;
          throw error;
        }
      }
      throw new Error('Could not create a battle room.');
    },
    [playerId, playerName, replaceRoom]
  );

  const joinAsGuest = useCallback(
    async (deck: string[], guestDeckPure: boolean): Promise<void> => {
      if (!roomId) return;
      const latest = (await fetchBattleRoom(roomId)) ?? roomRef.current;
      if (!latest) {
        setStatus('missing');
        return;
      }
      if (latest.abandonedBy) {
        replaceRoom(latest);
        return;
      }
      if (latest.host.id === playerId) return;
      if (latest.guest?.id === playerId) {
        replaceRoom(ensureBattle(latest));
        return;
      }
      if (latest.guest) {
        replaceRoom(latest);
        return;
      }
      const next = ensureBattle({
        ...latest,
        seq: latest.seq + 1,
        guest: {
          id: playerId,
          name: playerName,
          deck: [...deck],
          ...(guestDeckPure ? { isPure: true } : {}),
        },
      });
      await putBattleRoom(next);
      replaceRoom(next);
    },
    [playerId, playerName, replaceRoom, roomId]
  );

  const commitState = useCallback(
    (update: (current: BattleState) => BattleState, input?: BattleInput) => {
      const current = roomRef.current;
      if (!current?.state || current.abandonedBy) return;
      const state = update(current.state);
      if (state === current.state) return;
      const seq = current.seq + 1;
      const trail = input ? [...(current.trail ?? []), { seq, input }].slice(-BATTLE_TRAIL_LENGTH) : [];
      const next = { ...current, seq, state, trail };
      replaceRoom(next);
      enqueueWrite(next);
    },
    [enqueueWrite, replaceRoom]
  );

  const rematch = useCallback(() => {
    const current = roomRef.current;
    if (!current?.guest || current.abandonedBy) return;
    const seed = (current.seed + 1) % 1_000_000 || 1;
    const next: BattleRoom = {
      ...current,
      seed,
      seq: current.seq + 1,
      abandonedBy: null,
      trail: [],
      state: startPvpBattle({ ...current, seed, state: null, abandonedBy: null }),
    };
    replaceRoom(next);
    enqueueWrite(next);
  }, [enqueueWrite, replaceRoom]);

  const abandon = useCallback(
    (keepalive = false, source: 'pagehide' | 'leave' = 'leave') => {
      const current = roomRef.current;
      if (!current) return;
      if (source === 'pagehide' && !roomNeedsLeaveOnPageHide(current)) return;
      const conceded = concedeLiveRoom(current, playerId);
      if (conceded) {
        roomRef.current = conceded;
        seqRef.current = conceded.seq;
        void putBattleRoom(conceded, { keepalive });
        return;
      }
      const next = withAbandonedBy(current, playerId, playerName);
      if (!next) return;
      roomRef.current = next;
      seqRef.current = next.seq;
      void putBattleRoom(next, { keepalive });
    },
    [playerId, playerName]
  );
  const abandonRef = useRef(abandon);
  abandonRef.current = abandon;

  useEffect(() => {
    if (!roomId) return;
    const onPageHide = () => abandonRef.current(true, 'pagehide');
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      abandonRef.current(true);
    };
  }, [roomId]);

  const seat: PlayerId | null = room && playerId ? seatForPlayer(room, playerId) : null;
  const occupied = Boolean(room?.guest);
  const isFull = Boolean(room && occupied && seat == null && !room.abandonedBy);

  return {
    room,
    committed,
    status,
    seat,
    isFull,
    canJoin: Boolean(room && !occupied && seat == null && !room.abandonedBy),
    createChallenge,
    joinAsGuest,
    commitState,
    rematch,
    abandon,
  };
};
